import { selectCurrentReadingBook } from "./selectCurrentReadingBook";
import { isSavedReadingThemeCurrent, type SavedReadingThemeKey } from "./readingThemeDerive";
import type { UserBook } from "../../../services/firebase/booksService";

/**
 * Troca automática do tema da leitura (fatia 2, `docs/systems/reading-theme-auto.md`).
 * Só a DECISÃO mora aqui — pura, sem React, sem rede. O watcher liga os dados.
 */

/** Regenerações automáticas por sessão de processo (§3: contém flapping que escapou da histerese). */
export const AUTO_REGEN_SESSION_CAP = 2;
/**
 * Avanço num 2º livro "lendo" antes de ele tomar o tema (§3, histerese). Em
 * `progress`: página no físico, pontos percentuais no Kindle/áudio — 5 dos dois.
 */
export const AUTO_PAGES_HYSTERESIS = 5;

export type AutoBook = Pick<UserBook, "id" | "status" | "coverUrl" | "title" | "progress">;

export interface AutoDecisionInput {
  saved: SavedReadingThemeKey | null;
  /** A estante na ordem do contexto (`updatedAt desc`), a mesma do card de Ajustes. */
  books: readonly AutoBook[];
  /** 1ª avaliação da sessão: abrir o app É o gatilho — sem histerese. */
  isBoot: boolean;
  regensThisSession: number;
  /** Livro+capa já tentados nesta sessão (falha de capa/gate/CF não re-tenta a cada snapshot). */
  attempted: ReadonlySet<string>;
  /** Livros cujo status MUDOU entre o snapshot anterior e este (marcar "lendo"/"lido" = intenção). */
  statusChanged: ReadonlySet<string>;
  /** `progress` do candidato quando a histerese começou a observá-lo. */
  baselinePages: ReadonlyMap<string, number>;
}

export type AutoDecision =
  | { kind: "skip" }
  /** 2º livro "lendo" ainda sem atividade suficiente — guardar `pages` como linha de base. */
  | { kind: "wait"; bookId: string; pages: number }
  | { kind: "regen"; book: AutoBook; key: string };

export function autoAttemptKey(book: Pick<AutoBook, "id" | "coverUrl">): string {
  return `${book.id}|${book.coverUrl ?? ""}`;
}

export function decideReadingThemeAuto(input: AutoDecisionInput): AutoDecision {
  const current = selectCurrentReadingBook(input.books as UserBook[]);
  if (!current || !current.coverUrl) return { kind: "skip" };
  if (isSavedReadingThemeCurrent(input.saved, current)) return { kind: "skip" };
  if (input.regensThisSession >= AUTO_REGEN_SESSION_CAP) return { kind: "skip" };
  const key = autoAttemptKey(current);
  if (input.attempted.has(key)) return { kind: "skip" };

  // Mesmo livro, capa trocada (outra edição, capa própria) — BUG-297: troca na hora.
  const savedId = input.saved?.userBookId ?? null;
  if (savedId === current.id) return { kind: "regen", book: current, key };

  // Flapping (§3): o livro do tema segue "lendo" e o atual é OUTRO "lendo" que
  // só subiu por `updatedAt` (um toque de progresso). Na abertura do app ou
  // quando o status mudou agora (marcou "lendo"), troca na hora; senão, só
  // depois de AUTO_PAGES_HYSTERESIS de avanço nele.
  const savedBook = savedId ? input.books.find((b) => b.id === savedId) : undefined;
  const flapRisk = savedBook?.status === "reading" && current.status === "reading";
  if (!flapRisk || input.isBoot || input.statusChanged.has(current.id)) {
    return { kind: "regen", book: current, key };
  }
  const pages = typeof current.progress === "number" && Number.isFinite(current.progress) ? current.progress : 0;
  const baseline = input.baselinePages.get(current.id);
  if (baseline === undefined) return { kind: "wait", bookId: current.id, pages };
  return pages - baseline >= AUTO_PAGES_HYSTERESIS ? { kind: "regen", book: current, key } : { kind: "skip" };
}

/** Ids cujo status difere entre dois snapshots (livro novo na estante conta como mudança). */
export function changedStatusIds(
  prev: ReadonlyMap<string, string> | null,
  books: readonly Pick<AutoBook, "id" | "status">[],
): Set<string> {
  const out = new Set<string>();
  if (!prev) return out;
  for (const b of books) if (prev.get(b.id) !== b.status) out.add(b.id);
  return out;
}
